// Le coffre natif : Keychain sur iOS, Keystore sur Android.
//
// POURQUOI CE MODULE EXISTE
// -------------------------
// Jusqu'au 2026-08-28, la session d'un propriétaire ou d'un employé vivait dans
// `sessionStorage` (voir `authStorage.ts` et la trace du 2026-06-24). Sur mobile,
// la WebView vide ce tiroir quand le processus est tué : fermer l'app déconnectait.
// Les propriétaires ont demandé l'inverse. Ce module range le jeton dans le coffre
// chiffré du système, qui survit à la fermeture.
//
// Trois garde-fous, chacun payé par une erreur possible :
//  - `configure()` fixe explicitement l'accessibilité Keychain et coupe la
//    synchronisation iCloud. Ne JAMAIS se fier aux défauts du greffon : son défaut
//    est `whenUnlocked`, qui empêcherait toute lecture après un redémarrage tant que
//    l'appareil n'a pas été déverrouillé.
//  - Si le coffre est indisponible, on retombe EN MÉMOIRE, jamais sur un fichier en
//    clair. Écrire le jeton dans `localStorage` en secours annulerait tout l'intérêt
//    du chantier, et de façon invisible.
//  - `nativeGetItem` migre une session laissée par un build antérieur dans le
//    stockage web, pour ne pas déconnecter les clientes déjà connectées sur natif.

import { SecureStorage, KeychainAccess } from '@aparajita/capacitor-secure-storage';
import { isNative } from '@/lib/isNativePlatform';
// `sentry.ts` n'importe que `@sentry/react` (vérifié) : cet import ne crée aucun
// cycle avec `client.ts`, qui importe `authStorage.ts`, qui importe ce module.
import { captureException } from '@/lib/sentry';

// ⚠️ Dupliquée dans `authStorage.ts` (fonction `clearOwnerSessionSync`), même
// valeur, raison d'être différente (déconnexion natif ici, déconnexion web
// synchrone là-bas). Si l'une change sans l'autre, la déconnexion native et la
// déconnexion web divergent silencieusement. Pas d'export partagé : ce serait un
// couplage de plus pour peu de gain.
/** Clés de jeton Supabase : `sb-<ref>-auth-token`. */
const AUTH_TOKEN_RE = /^sb-.*-auth-token$/;

/** Horodatage de dernière utilisation, lu par la garde des 6 mois. */
export const LAST_USED_KEY = 'flowcut-last-used-at';

// Repli utilisé UNIQUEMENT si le coffre est indisponible. La session vit alors le
// temps du processus, ce qui reproduit exactement le comportement d'avant ce
// chantier : dégradation, pas régression de sécurité.
//
// INVARIANT : une clé n'est présente ici QUE si sa dernière écriture n'a PAS atteint
// le coffre (`writeNativeRaw` efface l'entrée dès que `SecureStorage.setItem` réussit ;
// `nativeRemoveItem` et `clearNativeAuthStore` la suppriment aussi). Une valeur présente
// en mémoire est donc TOUJOURS plus récente que ce que le coffre détient encore :
// `readNativeRaw` doit lui donner préséance, jamais l'inverse — sinon un jeton périmé
// resurgirait silencieusement (et un vieux refresh token déjà consommé déclenche la
// détection de réutilisation de GoTrue, qui révoque toute la session).
const memoryFallback = new Map<string, string>();

/** Témoin d'installation, voir `purgerSiInstallationNeuve` ci-dessous. */
const FIRST_RUN_KEY = 'flowcut_vault_installed';

// Le trousseau iOS SURVIT à la désinstallation de l'app : ses éléments restent
// attachés au groupe d'accès du bundle. Sans ce témoin, réinstaller Flowcut
// reconnecterait la personne sans rien saisir, éventuellement sur un téléphone
// revendu. Le témoin vit dans le stockage WEB, lui bien effacé avec l'app : son
// absence veut dire « installation neuve », donc coffre à vider.
//
// Il est posé ici, dans `configure()`, et pas dans un effet React : `auth-js` lit
// le stockage dès la construction du client, avant tout montage de composant.
async function purgerSiInstallationNeuve(): Promise<void> {
  let temoin: string | null = null;
  try {
    temoin = window.localStorage.getItem(FIRST_RUN_KEY);
  } catch {
    // Stockage web indisponible EN LECTURE : on ne peut pas distinguer une mise à
    // jour d'une installation neuve. On ne purge pas sur un simple doute (même
    // principe que partout ailleurs dans ce module : le faux positif coûte plus
    // cher que l'inaction), et on retentera de poser le témoin au prochain accès.
    return;
  }

  if (temoin !== null) return; // témoin déjà posé : rien à faire.

  // Témoin absent : soit une VRAIE installation neuve (coffre potentiellement
  // plein d'un jeton du propriétaire précédent), soit une mise à jour d'une app
  // qui n'a encore jamais eu de coffre (coffre déjà vide, la purge ne retire
  // rien). Les deux cas sont sans danger à traiter de la même façon.
  //
  // ORDRE IMPORTANT, NE PAS INVERSER : on pose le témoin D'ABORD, et on ne purge
  // le coffre QUE si cette écriture a réellement réussi. L'ordre inverse (purger
  // puis poser) a un défaut caché : si le stockage web est lisible mais PAS
  // inscriptible (quota de la WebView épuisé, pression de stockage sur
  // l'appareil), la lecture ci-dessus rend `null`, le coffre est purgé, puis
  // l'écriture du témoin jette et est avalée. `configuring` étant mémorisé par
  // processus, la purge ne boucle pas DANS la session, mais elle se rejoue à
  // CHAQUE lancement de l'app sur cet appareil : la personne reste connectée
  // pendant sa session puis se retrouve déconnectée à chaque réouverture,
  // indéfiniment. C'est un retour silencieux au comportement d'avant ce
  // chantier, introduit par le correctif de sécurité lui-même. En posant le
  // témoin en premier, un échec d'écriture arrête tout ici, avant la purge : on
  // préfère laisser une session ouverte qu'en tuer une à tort (même principe que
  // le repli mémoire et que `hasExceededInactivityWindow`).
  //
  // Fenêtre résiduelle assumée : entre la pose du témoin et la fin de la purge,
  // un processus tué sauterait la purge une seule fois. Quelques millisecondes
  // de risque contre une déconnexion permanente : l'arbitrage est clair.
  let poseReussie = false;
  try {
    window.localStorage.setItem(FIRST_RUN_KEY, '1');
    // On relit pour confirmer : certains stockages « acceptent » l'écriture sans
    // jeter mais ne retiennent rien (quota déjà plein selon l'implémentation).
    poseReussie = window.localStorage.getItem(FIRST_RUN_KEY) === '1';
  } catch {
    poseReussie = false;
  }
  if (!poseReussie) {
    // Écriture ratée : on ne purge PAS. On retentera de tout refaire (pose ET
    // purge) au prochain accès, tant que le témoin reste absent.
    return;
  }

  try {
    const all = await SecureStorage.keys();
    const cibles = all.filter((k) => AUTH_TOKEN_RE.test(k) || k === LAST_USED_KEY);
    for (const k of cibles) {
      try { await SecureStorage.removeItem(k); } catch { /* on continue */ }
    }
  } catch { /* le coffre est muet ; rien à purger de ce côté */ }
  memoryFallback.clear();
}

let configuring: Promise<void> | null = null;

async function configure(): Promise<void> {
  if (!configuring) {
    configuring = (async () => {
      await SecureStorage.setKeyPrefix('flowcut_');
      // `setSynchronize` est le SEUL des trois réglages à traverser le pont natif,
      // donc le seul qui puisse rejeter. `setDefaultKeychainAccess` passe en premier
      // pour que le réglage le plus important (voir plus haut : le défaut du greffon
      // est `whenUnlocked`, qui interdirait toute lecture avant le premier
      // déverrouillage) soit posé même si `setSynchronize` échoue ensuite.
      await SecureStorage.setDefaultKeychainAccess(KeychainAccess.afterFirstUnlockThisDeviceOnly);
      await SecureStorage.setSynchronize(false);
      await purgerSiInstallationNeuve();
    })();
  }
  try {
    await configuring;
  } catch {
    // Une configuration ratée ne doit pas être définitive : on la rejoue au
    // prochain accès plutôt que de laisser le coffre mal réglé pour toute la
    // durée de vie de l'app.
    configuring = null;
  }
}

function webStores(): Storage[] {
  const stores: Storage[] = [];
  try { stores.push(window.localStorage); } catch { /* stockage indisponible */ }
  try { stores.push(window.sessionStorage); } catch { /* stockage indisponible */ }
  return stores;
}

function readWeb(key: string): string | null {
  for (const store of webStores()) {
    try {
      const value = store.getItem(key);
      if (value !== null) return value;
    } catch { /* on essaie l'autre */ }
  }
  return null;
}

function removeWeb(key: string): void {
  for (const store of webStores()) {
    try { store.removeItem(key); } catch { /* rien à faire */ }
  }
}

// Réservée aux appelants qui gardent déjà l'accès natif en amont (`nativeGetItem`,
// `hasExceededInactivityWindow`, etc.). Contrairement à `clearNativeAuthStore`, elle
// ne porte PAS elle-même la garde `isNative()` : un futur appelant qui l'oublierait
// écrirait un secret EN CLAIR dans le `localStorage` du web, puisque
// l'implémentation web du greffon fait exactement ça.
/** Lecture brute du coffre, sans migration. Ne jette jamais. */
export async function readNativeRaw(key: string): Promise<string | null> {
  await configure();
  // Préséance à la mémoire : si la clé y est présente, c'est qu'une écriture plus
  // récente n'a pas atteint le coffre (voir l'invariant au-dessus de `memoryFallback`).
  if (memoryFallback.has(key)) return memoryFallback.get(key)!;
  try {
    const value = await SecureStorage.getItem(key);
    if (value !== null && value !== undefined) return value;
  } catch { /* le coffre est muet et rien n'est en mémoire ; on rend null plus bas */ }
  return null;
}

// Signalé une seule fois par durée de vie de l'app : si le greffon natif n'est pas
// correctement enregistré dans le binaire, CHAQUE écriture jette et le repli
// mémoire prend le relais en silence (les `catch` du module ne jettent jamais).
// Les sessions redeviendraient alors mortelles à la fermeture de l'app, pour tout
// le monde en même temps, sans aucun log ni compteur : le seul moyen de s'en
// apercevoir serait un contrôle manuel sur appareil. Un seul événement Sentry, à
// la première occurrence, suffit à le détecter sans inonder le projet à chaque
// écriture (jeton, poinçon d'activité...) tant que le coffre reste indisponible.
let coffreIndisponibleSignale = false;

// Même réserve que `readNativeRaw` juste au-dessus : pas de garde `isNative()`
// ici non plus, tous les appelants actuels la portent déjà en amont. Ne PAS
// ajouter la garde ici : ça changerait le comportement des tests existants, qui
// exercent cette fonction directement.
/**
 * Écriture brute dans le coffre. Ne jette jamais.
 * Rend `true` quand `SecureStorage.setItem` a réellement réussi, `false` sinon (la
 * valeur est alors dans le repli mémoire). Les appelants qui doivent savoir si le
 * coffre a VRAIMENT accepté l'écriture (ex. la migration dans `nativeGetItem`, qui
 * n'a le droit d'effacer la copie web que si le coffre en tient une copie durable)
 * se fient à ce booléen plutôt qu'à l'absence d'exception.
 */
export async function writeNativeRaw(key: string, value: string): Promise<boolean> {
  await configure();
  try {
    await SecureStorage.setItem(key, value);
    memoryFallback.delete(key);
    return true;
  } catch (err) {
    memoryFallback.set(key, value);
    if (!coffreIndisponibleSignale) {
      coffreIndisponibleSignale = true;
      try {
        // `captureException` est une bibliothèque tierce non enveloppée par ce
        // module : la garantie « ne jette jamais » ne doit pas dépendre de son
        // bon comportement, elle doit tenir même si elle jette.
        captureException(
          err instanceof Error ? err : new Error(`SecureStorage.setItem failed: ${String(err)}`),
          { where: 'writeNativeRaw' },
        );
      } catch { /* le signal est perdu, mais la garantie « ne jette jamais » tient */ }
    }
    return false;
  }
}

export async function nativeGetItem(key: string): Promise<string | null> {
  const fromVault = await readNativeRaw(key);
  if (fromVault !== null) return fromVault;

  // Migration unique : un build antérieur a pu laisser la session dans le stockage
  // web. On la remonte dans le coffre puis on efface la copie en clair.
  const legacy = readWeb(key);
  if (legacy === null) return null;

  const ok = await writeNativeRaw(key, legacy);
  // On n'efface la copie web QUE si le coffre l'a réellement acceptée. Sinon elle
  // reste la seule copie durable : l'effacer condamnerait la session à mourir à la
  // fermeture de l'app, ce que ce chantier existe précisément pour empêcher.
  //
  // Ça ne rouvre pas la résurrection d'un jeton périmé : tant que la copie web
  // survit ici, elle est identique à ce qui vient d'être posé en mémoire par
  // `writeNativeRaw` (même `legacy`), et la toute prochaine écriture réussie
  // l'effacera via `nativeSetItem`, qui efface la copie web inconditionnellement.
  // L'invariant de préséance mémoire (voir au-dessus de `memoryFallback`) continue
  // de tenir : mémoire et copie web disent la même chose jusqu'à ce moment-là.
  if (ok) removeWeb(key);
  return legacy;
}

export async function nativeSetItem(key: string, value: string): Promise<void> {
  await writeNativeRaw(key, value);
  // Ne jamais laisser une copie en clair derrière soi.
  removeWeb(key);
}

export async function nativeRemoveItem(key: string): Promise<void> {
  await configure();
  memoryFallback.delete(key);
  removeWeb(key);
  try {
    await SecureStorage.removeItem(key);
  } catch { /* la clé n'existait peut-être pas ; rien à rattraper */ }
}

/**
 * Efface tout ce qui pourrait rouvrir une session : les jetons et l'horodatage.
 * Utilisé par la déconnexion et par la garde des 6 mois.
 */
export async function clearNativeAuthStore(): Promise<void> {
  // `AuthContext.signOut` nous appelle sur TOUTES les plateformes. Sans cette garde,
  // le web irait taper l'implémentation web du greffon, qu'on n'utilise nulle part.
  if (!isNative()) return;

  await configure();
  memoryFallback.clear();

  try {
    const all = await SecureStorage.keys();
    const cibles = all.filter((k) => AUTH_TOKEN_RE.test(k) || k === LAST_USED_KEY);
    for (const k of cibles) {
      try { await SecureStorage.removeItem(k); } catch { /* on continue */ }
      removeWeb(k);
    }
  } catch { /* le coffre est muet ; le repli mémoire est déjà vidé */ }
}

/** Remise à zéro entre deux tests. */
export function __resetNativeAuthStoreForTests(): void {
  memoryFallback.clear();
  configuring = null;
  coffreIndisponibleSignale = false;
}
